package dev.lingvo.site.pages.app.projects.slug.screenshots

import dev.lingvo.shared.StorePlatform
import dev.lingvo.shared.WorkspaceDto
import dev.lingvo.site.api.Session
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectPrivilege
import dev.lingvo.site.state.VerifiedProjectRoute
import dev.lingvo.site.state.liveProjectRoute
import kotlinx.coroutines.CancellationException
import dev.lingvo.site.i18n.t

/** Personal subscriptions require membership; workspace delivery and public sharing require admin. */
internal enum class ScreenshotUpdatesAction { PERSONAL_EMAIL, WORKSPACE_NOTIFICATIONS, REPORT_SHARE }

/** Owns mutations until completion or disposal so obsolete responses cannot republish a share token. */
internal class ScreenshotUpdatesRunner(
    slug: String,
    private val platform: StorePlatform,
    private val launch: (suspend () -> Unit) -> Unit,
    private val currentRoute: () -> VerifiedProjectRoute? = { liveProjectRoute(slug) },
    private val memberships: () -> List<WorkspaceDto> = { Session.organizations },
    private val personSession: () -> Boolean = { Session.isAuthenticated && Session.token?.split('.')?.size == 3 },
    private val currentPlatform: () -> StorePlatform = { platform },
) {
    private var active = true
    private val pending = mutableSetOf<ScreenshotUpdatesAction>()
    private val claims = ScreenshotUpdatesAction.entries.associateWith { action ->
        ProjectActionClaim.capture(currentRoute(), memberships(),
            if (action == ScreenshotUpdatesAction.PERSONAL_EMAIL) ProjectPrivilege.READ else ProjectPrivilege.STRUCTURE)
    }

    /** Checks before enqueue, coroutine entry, and every callback; mutation failures never retry. */
    fun <T> run(
        action: ScreenshotUpdatesAction,
        setBusy: (Boolean) -> Unit,
        transport: suspend () -> T,
        onFailure: (Throwable) -> Unit,
        onSuccess: (T) -> Unit,
    ) {
        val claim = claims[action] ?: return
        if (!authorized(claim) || !pending.add(action)) return
        launch {
            if (!authorized(claim)) { pending.remove(action); return@launch }
            setBusy(true)
            try {
                val value = transport()
                if (authorized(claim)) onSuccess(value)
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: Throwable) {
                if (authorized(claim)) onFailure(failure)
            } finally {
                pending.remove(action)
                if (authorized(claim)) setBusy(false)
            }
        }
    }

    /** Disposal retires queued work and values even when a fetch implementation ignores cancellation. */
    fun close() { active = false }

    private fun authorized(claim: ProjectActionClaim): Boolean = active && personSession() &&
        currentPlatform() == platform && claim.remainsAuthorized(currentRoute(), memberships())
}

/** Only the matching public aggregate endpoint can become embeddable HTML; labels are escaped. */
internal fun auditShareMarkup(
    reportUrl: String,
    badgeUrl: String,
    label: String = tr("app.projects.slug.screenshots.updates_model.screenshot_audit_report", "Screenshot audit report"),
): String? {
    val route = Regex("^(https?://(?:[A-Za-z0-9.-]+|\\[[A-Fa-f0-9:]+\\])(?::[0-9]{1,5})?)(/api/v1/screenshot-reports/[A-Za-z0-9_-]+)$")
    if (!route.matches(reportUrl) || badgeUrl != "$reportUrl/badge.svg") return null
    val safeLabel = label.replace("&", "&amp;").replace("\"", "&quot;").replace("<", "&lt;").replace(">", "&gt;")
    return "<a href=\"$reportUrl\"><img src=\"$badgeUrl\" alt=\"$safeLabel\" /></a>" // i18n-ignore
}
